import type { ErrorEvent } from "@sentry/core";
import type { NodeOptions } from "@sentry/node";

// Vercel's x-vercel-sc-headers carries an Authorization bearer token inside its JSON value.
const SENSITIVE_HEADER_SNIPPETS = ["auth", "token", "cookie", "key", "secret", "signature", "session", "sc-headers"];

const stripQueryAndFragment = (url: string): string => url.split(/[?#]/, 1)[0] ?? url;

// dataCollection covers neither the always-kept request URL and Referer header, the request_path Next's
// captureRequestError writes into contexts.nextjs (all carry the raw query string), the incoming body the http
// integration attaches, nor sensitive headers: its header deny-list only reaches span attributes, not error events.
export const stripRequestData = (event: ErrorEvent): ErrorEvent => {
  if (event.request !== undefined) {
    delete event.request.data;
    if (event.request.url !== undefined) {
      event.request.url = stripQueryAndFragment(event.request.url);
    }
    const headers = event.request.headers ?? {};
    for (const name of Object.keys(headers)) {
      const lowerName = name.toLowerCase();
      const value = headers[name];
      if (SENSITIVE_HEADER_SNIPPETS.some((snippet) => lowerName.includes(snippet))) {
        delete headers[name];
      } else if (lowerName === "referer" && value !== undefined) {
        headers[name] = stripQueryAndFragment(value);
      }
    }
  }
  const requestPath = event.contexts?.nextjs?.request_path;
  if (event.contexts?.nextjs !== undefined && typeof requestPath === "string") {
    event.contexts.nextjs.request_path = stripQueryAndFragment(requestPath);
  }
  return event;
};

// The Next.js apps keep non-sensitive request headers (User-Agent feeds browser tags); omitted fields default to
// collecting, and an explicit requestDataIntegration `include` overrides these. 7 context lines is the SDK default
// without it.
export const NEXTJS_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpBodies: [],
  queryParams: false,
  httpHeaders: { request: { deny: SENSITIVE_HEADER_SNIPPETS }, response: { deny: ["cookie", "token", "auth"] } },
  frameContextLines: 7,
} satisfies NodeOptions["dataCollection"];

// For apps whose requests carry homeowner context: Sentry gets errors only, with no request data, breadcrumb data or
// spans. Setting dataCollection makes the SDK ignore the shared config's sendDefaultPii.
export const STRICT_SENTRY_PRIVACY = {
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: false, response: false },
    httpBodies: [],
    queryParams: false,
    stackFrameVariables: false,
  },
  beforeSend: stripRequestData,
  beforeSendTransaction: () => null,
  beforeBreadcrumb: (breadcrumb) =>
    breadcrumb.category === "console" || breadcrumb.type === "http" ? null : { ...breadcrumb, data: undefined },
} satisfies NodeOptions;
